import { Row } from "components/Layout";
import { Field } from "formik";
import {
  SelectField,
  TextArea,
  withNamespace,
} from "components/Form/FormikControls";
import { groupedLineItemToolTipText } from "constants/billing";
import React, { useState } from "react";
import { v4 } from "uuid";
import { useFieldSetter, useFieldValue } from "hooks";
import { useDispatch, useSelector } from "react-redux";
import {
  getRulesByRuleBookId,
  getMasterRulesByMasterRuleBookId,
  getRuleSetOptionsByRuleBookId,
  getMasterRuleSetOptionsByMasterRuleBookId,
} from "selectors";
import { DataTypeMenu } from "containers/Settings/RuleBooks/DataSource/DataTypeMenu";
import {
  selectPrerequisiteMasterRuleChainByMasterRuleIdLookup,
  selectPrerequisiteRuleChainByRuleIdLookup,
} from "containers/Settings/RuleBooks/Schema/livestockSaleSchema";
import {
  getDataTypeByPath,
  normalisePathParts,
} from "containers/Settings/RuleBooks/Schema/schema";
import { useRuleFieldSchema } from "containers/Settings/RuleBooks/Schema/schemaContext";
import { convertToSchemaPath } from "containers/Settings/RuleBooks/import";
import { Grid } from "@material-ui/core";
import { MultiButton } from "components/Button";
import { faPencil, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { openDispatchingModal } from "actions";
import { ModalTypes } from "constants/navigation";

export interface GeneralFormValues {
  // The name of the Rule
  name: string;

  // A String categorisation of the rule, used for Saleyard Billing to drive grouping of ledger entries in some reports
  category: string;

  // A comment to be displayed alongside the rule, helping users understand what the rule does, and how it works
  comment: string;

  // A template for the title the Ledger Entries created by this rule
  title_template: string;

  // A template for the description of the line item in the invoice.
  // When this is set, the all ledger entries on the same document, sharing the same title will be summed together and shown as a single line item
  invoice_line_item_template: string;

  // A list of billing tags that will be applied to all Ledger Entries output by this rule
  // These are used for filtering and reporting at a later stage. They drive where the line items are shown in the generated invoices and rctis
  // e.g. SaleLotTransaction tagged ledger entries will be shown in the primary table of the document
  billing_tags: Array<string>;

  // id linked to the rule set this rule belongs to
  rule_set_id: string | null;
}

const categoryDataListId = v4();

interface GeneralFormProps {
  // A List of Options the user can select from for the Billing Tags field
  billingTags: Array<{ id: string; name: string }>;

  // A distinct list of categories from the other rules in the same rule book
  categories: Array<string>;

  isMasterRule: boolean;

  fullInputPath: string;

  // The ID of the rule being edited (when not on create mode
  ruleId?: string;

  namespace?: string;
}

export function GeneralForm(props: GeneralFormProps) {
  const {
    billingTags,
    categories,
    isMasterRule,
    ruleId = null,
    namespace: ns = "",
    fullInputPath,
  } = props;
  const dispatch = useDispatch();

  const isCreate = !ruleId;

  const ruleBookId: string = useFieldValue("rule_book_id");
  const ruleSetId: string | null = useFieldValue("rule_set_id");

  const setInvoiceLineItemTemplate = useFieldSetter(
    withNamespace(ns, "invoice_line_item_template"),
  );
  const ruleChainByIdLookup = useSelector(
    isMasterRule
      ? selectPrerequisiteMasterRuleChainByMasterRuleIdLookup
      : selectPrerequisiteRuleChainByRuleIdLookup,
  );
  const setTitleTemplate = useFieldSetter(withNamespace(ns, "title_template"));

  const ruleSetOptions =
    useSelector(
      isMasterRule
        ? getMasterRuleSetOptionsByMasterRuleBookId(ruleBookId)
        : getRuleSetOptionsByRuleBookId(ruleBookId),
    ) || [];

  const schema = useRuleFieldSchema();

  const rules: Rule[] | MasterRule[] =
    useSelector(
      isMasterRule
        ? getMasterRulesByMasterRuleBookId(ruleBookId)
        : getRulesByRuleBookId(ruleBookId),
    ) || [];

  const [anchorEl, setAnchorEl] = useState(null);
  const [fieldName, setFieldName] = useState(null);

  function onChangeTemplateField(event) {
    // check if the last two characters are "{{", if they are show the DataSouce Menu for the input field
    const newTemplate = event.target.value;
    const position = event.target.selectionStart;
    if (position > 1 && newTemplate.slice(position - 2, position) === "{{") {
      // show the DataSource Menu for the input field
      setFieldName(event.currentTarget.name);
      setAnchorEl(event.currentTarget);
    }
  }

  // Resolve the datatype of the prerequisite ledger entry to use as a filter for the selectable rules
  // Only show rules that are of the same type as the first prerequisite rule
  const firstPrerequisiteRuleId =
    useFieldValue("prerequisite_rule_ids")?.[0] || null;
  const ruleChain = ruleChainByIdLookup[firstPrerequisiteRuleId];
  const ruleChainPath = convertToSchemaPath(
    normalisePathParts(ruleChain?.inputPathParts || []),
  );
  const ruleChainDataType = getDataTypeByPath(schema, ruleChainPath);

  const ruleOptions = Object.values(rules)
    .filter(rule => {
      if (rule.id === ruleId) {
        return false;
      }

      if (!firstPrerequisiteRuleId) {
        return true;
      }
      const otherRuleChain = ruleChainByIdLookup[rule.id];
      const otherRuleInputPath = convertToSchemaPath(
        normalisePathParts(otherRuleChain?.inputPathParts || []),
      );
      const otherRuleChainDataType = getDataTypeByPath(
        schema,
        otherRuleInputPath,
      );
      if (!otherRuleChainDataType) {
        return false;
      }
      return ruleChainDataType.id === otherRuleChainDataType.id;
    })
    .map((rule: Rule | MasterRule) => ({
      label: rule.name,
      value: rule.id,
    }));

  const handleEditMasterRuleSet = () => {
    dispatch(
      openDispatchingModal(ModalTypes.EditMasterRuleSet, window.location.hash, {
        masterRuleSetId: ruleSetId,
        masterRuleId: ruleId,
        previousModalContextKey: ModalTypes.EditMasterRule,
      }),
    );
  };

  const handleEditRuleSet = () => {
    dispatch(
      openDispatchingModal(ModalTypes.EditRuleSet, window.location.hash, {
        ruleSetId,
        ruleId,
        previousModalContextKey: ModalTypes.EditRule,
        useBasicForm: true,
      }),
    );
  };

  const handleCreateMasterRuleSet = () => {
    dispatch(
      openDispatchingModal(ModalTypes.EditMasterRuleSet, window.location.hash, {
        newRuleSet: {
          criteria: [],
          id: v4(),
          input_field: null,
          name: "",
        },
        masterRuleId: ruleId,
        previousModalContextKey: ModalTypes.EditMasterRule,
      }),
    );
  };

  const handleCreateRuleSet = () => {
    dispatch(
      openDispatchingModal(ModalTypes.EditRuleSet, window.location.hash, {
        newRuleSet: {
          criteria: [],
          id: v4(),
          input_field: null,
          name: "",
        },
        ruleId,
        previousModalContextKey: ModalTypes.EditRule,
      }),
    );
  };

  const ruleSetActions = [
    {
      title: "Edit Rule Set",
      isDisabled: !ruleSetId,
      onClick: isMasterRule ? handleEditMasterRuleSet : handleEditRuleSet,
      default: true,
      icon: faPencil,
      dataTour: isMasterRule ? "editMasterRuleSet" : "editRuleSet",
    },
    {
      title: "Add Rule Set",
      isDisabled: false,
      onClick: isMasterRule ? handleCreateMasterRuleSet : handleCreateRuleSet,
      default: false,
      icon: faPlus,
      dataTour: isMasterRule ? "addMasterRuleSet" : "addRuleSet",
    },
  ];

  return (
    <>
      <Row full alignCenter paddingVertical={1}>
        <span>Name:</span>&nbsp;
        <Field
          data-tour="name"
          className="p-2 flex-1"
          name={withNamespace(ns, "name")}
        />
      </Row>
      <Row full alignCenter paddingVertical={1}>
        <span>Prerequisite Rules:</span>
        <SelectField
          name={withNamespace(ns, "prerequisite_rule_ids")}
          options={ruleOptions}
          isMulti
        />
      </Row>
      <Grid container item xs={12} alignItems="center">
        <Grid item xs={isCreate ? 12 : 10}>
          <Row alignCenter paddingVertical={1} marginRight={1}>
            <span>Rule Set:</span>
            <SelectField
              isClearable
              name={withNamespace(ns, "rule_set_id")}
              options={ruleSetOptions}
            />
          </Row>
        </Grid>
        {!isCreate && (
          <Grid item xs={2}>
            <MultiButton buttons={ruleSetActions} />
          </Grid>
        )}
      </Grid>
      <Row full alignCenter paddingVertical={1}>
        <span>Category:</span>&nbsp;
        <Field
          data-tour="category"
          className="p-2 flex-1"
          list={categoryDataListId}
          name={withNamespace(ns, "category")}
        />
        <datalist id={categoryDataListId}>
          {categories.map((category: string) => (
            <option key={`${category}`}>{category}</option>
          ))}
        </datalist>
      </Row>
      <Row full alignCenter paddingVertical={1}>
        <span>Comment:</span>&nbsp;
        <Field
          data-tour="comment"
          className="p-2 flex-1"
          name={withNamespace(ns, "comment")}
          as="textarea"
        />
      </Row>
      <Row full alignCenter paddingVertical={1}>
        <TextArea
          label="Title Template"
          name={withNamespace(ns, "title_template")}
          onAfterChanged={onChangeTemplateField}
        />
      </Row>
      <Row full alignCenter paddingVertical={1}>
        <TextArea
          tooltip={groupedLineItemToolTipText}
          label="Document Grouped Line Description"
          name={withNamespace(ns, "invoice_line_item_template")}
          onAfterChanged={onChangeTemplateField}
        />
      </Row>
      <Row data-tour="billingTag" alignEnd paddingVertical={1}>
        <span>Billing Tag:</span>
        <SelectField
          name={withNamespace(ns, "billing_tags")}
          options={billingTags}
          tooltip="A tag that will be applied to all Ledger Entries output by this rule. These can be used for filtering and reporting at a later stage."
          isMulti
        />
      </Row>
      <DataTypeMenu
        dataTour="path-helper"
        anchorEl={anchorEl}
        depth={0}
        fieldPath={fullInputPath}
        onClose={() => {
          setAnchorEl(null);
          setFieldName(null);
        }}
        onSelect={(path: string) => {
          // TODO To improve the UX, this should find the next set of closing
          //  brackets, so long as there are not any open brackets between the
          //  start and the closing brackets
          const newTemplate = `${anchorEl.value} ${path} }}`;
          if (fieldName === "invoice_line_item_template") {
            setInvoiceLineItemTemplate(newTemplate);
          } else {
            setTitleTemplate(newTemplate);
          }
          setAnchorEl(null);
          setFieldName(null);
        }}
        showManyRelations
        showProperties
        showRelations
        schema={schema}
        via=""
      />
    </>
  );
}
